import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Brain, ChevronRight, Save, Home, Lightbulb, Target, Loader2, Sparkles, TrendingUp, Shield, CheckCircle2, CheckCircle, Trophy, Zap, ArrowLeft, FileText, Download, ClipboardList, Clock, X, Tag, User, GraduationCap, Building, FileCheck, AlertCircle } from 'lucide-react';
import { usePersona } from '../contexts/PersonaContext';
import ImpactCategorySelector from '../components/ImpactCategorySelector';
import { personas } from '../contexts/PersonaContext';
import { getPersonaPrompts, getIntroPrompt } from '../personaPrompts';
import { toast } from '../hooks/use-toast';
import SubmissionModal from '../components/SubmissionModal';
import ThinkLab from '../components/ThinkLab';
import { trackPageView, trackSessionStart, trackFeatureUse } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to track guest sessions for later claiming
const trackGuestSession = (sessionId) => {
  try {
    const existingIds = JSON.parse(localStorage.getItem('guest_session_ids') || '[]');
    if (!existingIds.includes(sessionId)) {
      existingIds.push(sessionId);
      localStorage.setItem('guest_session_ids', JSON.stringify(existingIds));
    }
  } catch (e) {
    console.error('Failed to track guest session:', e);
  }
};

// Generate challenge-specific prompts
const generateChallengePrompts = (challenge) => {
  const sdgName = challenge.sdg_name;
  const title = challenge.title;
  const description = challenge.description;
  
  return {
    spark: [
      `How would you approach solving ${title.toLowerCase()}? What's your initial idea?`,
      `What innovative approach could address ${sdgName}? Think beyond conventional solutions.`,
      `If you had unlimited resources, how would you tackle this challenge: ${description}`,
      `What if you combined technology and community engagement to solve this? What would that look like?`,
      `What's a completely different angle on ${sdgName} that hasn't been tried before?`
    ],
    mirror: [
      `Who are the key stakeholders affected by ${sdgName}? How does your solution serve them?`,
      `What similar solutions exist in the ${challenge.tags[0]} space? How is yours different or better?`,
      `How would you validate that your solution actually addresses ${title.toLowerCase()}?`,
      `What potential unintended consequences should you consider for your ${sdgName} solution?`,
      `How would you measure the real-world impact of your solution on ${sdgName}?`
    ],
    iteration: [
      `How could you make your ${sdgName} solution more scalable across different contexts?`,
      `What partnerships or resources would accelerate your solution's impact on ${title.toLowerCase()}?`,
      `If you had to implement this solution in the next 6 months, what would your roadmap look like?`,
      `How could you make your solution more sustainable and self-sufficient over time?`,
      `What technology or methodology could make your ${sdgName} solution 10x more effective?`
    ]
  };
};

// Simplified stages - just thinking prompts
const stages = [
  {
    key: 'spark',
    title: 'Spark Activation',
    icon: Lightbulb,
    color: 'from-yellow-500 to-orange-500',
    description: 'Generate your initial idea through "What If" prompts',
    prompts: 3
  },
  {
    key: 'mirror',
    title: 'Mirror Validation',
    icon: Target,
    color: 'from-blue-500 to-cyan-500',
    description: 'Validate and refine your idea',
    prompts: 3
  },
  {
    key: 'iteration',
    title: 'Iteration & Refinement',
    icon: Shield,
    color: 'from-purple-500 to-pink-500',
    description: 'Polish and strengthen your concept',
    prompts: 2
  }
];

const NewThinkingSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get('challenge');
  const { getActivePersonas, getAllActivePersonasData, togglePersona } = usePersona();
  const { isAuthenticated } = useAuth();
  
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [sessionTitle, setSessionTitle] = useState('');
  const [showTitleInput, setShowTitleInput] = useState(!urlSessionId || urlSessionId === 'new');
  const [brainDump, setBrainDump] = useState('');
  const [introPrompt, setIntroPrompt] = useState('');
  const [answers, setAnswers] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [loadingSession, setLoadingSession] = useState(!!urlSessionId && urlSessionId !== 'new');
  
  // Contextual questions state
  const [contextualQuestions, setContextualQuestions] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  
  // Document upload state
  const [showUploadOption, setShowUploadOption] = useState(!urlSessionId || urlSessionId === 'new');
  const [uploadedDoc, setUploadedDoc] = useState(null);
  const [docAnalysis, setDocAnalysis] = useState(null);
  const [customPrompts, setCustomPrompts] = useState(null);
  const [showPersonaSelector, setShowPersonaSelector] = useState(false);
  const [showThinkLab, setShowThinkLab] = useState(false);
  
  // Challenge-specific state
  const [challengeData, setChallengeData] = useState(null);
  const [challengePrompts, setChallengePrompts] = useState(null);
  
  // Timer state shared with ThinkLab
  const [timerActive, setTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [activeReading, setActiveReading] = useState(null);
  const [showReturnPrompt, setShowReturnPrompt] = useState(false);
  const [trackedReadings, setTrackedReadings] = useState([]);
  const [activeTab, setActiveTab] = useState('brainstorm');
  const [readingSummary, setReadingSummary] = useState(null);

  // Idea Report state
  const [generatingReport, setGeneratingReport] = useState(false);
  const [structuredReport, setStructuredReport] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [generatingProposal, setGeneratingProposal] = useState(false);
  const [proposalData, setProposalData] = useState(null);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedProposalType, setSelectedProposalType] = useState('irb');
  
  // Impact Category state (for privacy-preserving analytics)
  const [impactCategory, setImpactCategory] = useState('');

  // Load existing session when navigating to /session/:id
  useEffect(() => {
    if (!urlSessionId || urlSessionId === 'new') return;
    
    const loadExistingSession = async () => {
      setLoadingSession(true);
      try {
        const token = localStorage.getItem('if_auth_token');
        const config = {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        };
        
        const res = await axios.get(`${BACKEND_URL}/api/sessions/${urlSessionId}`, config);
        const session = res.data;
        
        if (session) {
          setSessionId(session.id);
          setSessionTitle(session.title || '');
          setShowTitleInput(false);
          setShowUploadOption(false);
          
          // Restore answers from stage_answers
          if (session.stage_answers) {
            const restored = {};
            Object.entries(session.stage_answers).forEach(([stage, stageAnswers]) => {
              Object.entries(stageAnswers || {}).forEach(([idx, val]) => {
                restored[`${stage}-${idx}`] = val;
              });
            });
            setAnswers(restored);
          }
          
          // Restore analysis results (OI scores)
          if (session.oi_scores) {
            const oi = session.oi_scores;
            const msa = session.msa_scores || {};
            setAnalysisResults({
              oi: {
                total: oi.total_oi || oi.total || 0,
                novelty: oi.novelty || 0,
                resonance: oi.resonance || 0,
                synthesis: oi.synthesis || 0,
                ethical: oi.ethical || 0,
                interpretation: oi.interpretation || '',
                formula: oi.formula || 'OI = (0.3 × Novelty) + (0.3 × Resonance) + (0.2 × Synthesis) + (0.2 × Ethical)',
                novelty_reasoning: oi.novelty_reasoning || '',
                resonance_reasoning: oi.resonance_reasoning || '',
                synthesis_reasoning: oi.synthesis_reasoning || '',
                ethical_reasoning: oi.ethical_reasoning || ''
              },
              msa: {
                spark_pct: msa.spark_pct || 50,
                mirror_pct: msa.mirror_pct || 50,
                assessment: msa.assessment || msa.interpretation || 'Balanced'
              }
            });
            // Go to results stage
            setCurrentStageIndex(stages.length - 1);
          }
          
          // Restore structured report
          if (session.structured_report) {
            setStructuredReport(session.structured_report);
          } else if (session.status === 'completed' || session.oi_scores?.total_oi > 0 || session.oi_scores?.total > 0) {
            // Auto-generate report if session is completed but no report exists
            try {
              const reportRes = await axios.post(
                `${BACKEND_URL}/api/sessions/${session.id}/generate-report`,
                {},
                config
              );
              if (reportRes.data.success) {
                setStructuredReport(reportRes.data.report);
              }
            } catch (reportErr) {
              console.error('Auto-report generation failed:', reportErr);
            }
          }
        }
      } catch (error) {
        console.error('Error loading session:', error);
        if (error.response?.status === 404) {
          toast({
            title: "Session Not Found",
            description: "This session doesn't exist or you don't have access.",
            variant: "destructive"
          });
        }
      } finally {
        setLoadingSession(false);
      }
    };
    
    loadExistingSession();
  }, [urlSessionId]);

  // Track page view
  useEffect(() => {
    trackPageView('/session/new');
  }, []);
  
  // Fetch challenge data if challenge param exists
  useEffect(() => {
    const fetchChallengeData = async () => {
      if (challengeId) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/challenges/${challengeId}`);
          const challenge = response.data;
          setChallengeData(challenge);
          
          // Pre-fill session title with challenge title
          setSessionTitle(`Solution for: ${challenge.title}`);
          setShowTitleInput(false); // Skip the title input step
          
          // Generate AI-powered contextual questions for this specific challenge
          generateChallengeContextualQuestions(challenge);
          
          // Set intro prompt for challenge
          setIntroPrompt(`You're working on: ${challenge.title}\n\n${challenge.description}`);
          
        } catch (error) {
          console.error('Error fetching challenge:', error);
        }
      }
    };
    
    fetchChallengeData();
  }, [challengeId]);
  
  // Generate AI-powered questions specifically for a challenge
  const generateChallengeContextualQuestions = async (challenge) => {
    setGeneratingQuestions(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/generate-challenge-questions`, {
        challenge_id: challenge.id,
        challenge_title: challenge.title,
        challenge_description: challenge.description,
        sdg_name: challenge.sdg_name,
        tags: challenge.tags || []
      });
      
      if (response.data.success && response.data.questions) {
        // Set as custom prompts (highest priority)
        setCustomPrompts(response.data.questions);
        setChallengePrompts(response.data.questions); // Also set as challenge prompts for backup
        toast({
          title: "🎯 Challenge Questions Ready",
          description: `Personalized prompts for "${challenge.title.substring(0, 30)}..."`,
        });
      } else {
        // Fallback to static challenge prompts
        const staticPrompts = generateChallengePrompts(challenge);
        setChallengePrompts(staticPrompts);
      }
    } catch (error) {
      console.error('Error generating challenge questions:', error);
      // Fallback to static challenge prompts
      const staticPrompts = generateChallengePrompts(challenge);
      setChallengePrompts(staticPrompts);
    } finally {
      setGeneratingQuestions(false);
    }
  };

  // Set dynamic intro prompt based on active personas (only if not a challenge)
  useEffect(() => {
    if (!challengeId) {
      const activePersonas = getAllActivePersonasData();
      const prompt = getIntroPrompt(activePersonas);
      setIntroPrompt(prompt);
    }
  }, [getAllActivePersonasData, challengeId]);

  // Detect when user returns from reading
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && activeReading && trackingEnabled) {
        // User returned to the tab
        const elapsed = Math.floor((Date.now() - new Date(activeReading.startTime)) / 60000);
        const timebox = activeReading.timeboxMinutes || 30;
        
        // Show return prompt for ANY return when tracking is enabled
        if (timerActive) {
          // Generate quick summary
          try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/thinklab/reading-summary`, {
              resource_title: activeReading.resource.title,
              resource_url: activeReading.resource.url,
              time_spent_minutes: elapsed,
              session_id: sessionTitle
            });
            
            setReadingSummary(response.data.summary);
          } catch (error) {
            console.error('Summary generation failed:', error);
            setReadingSummary(null);
          }
          
          setShowReturnPrompt(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeReading, timerActive, trackingEnabled]);

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if coming from Quick Evaluate
  useEffect(() => {
    if (location.state?.fromEvaluation) {
      const { paperContent, paperTitle } = location.state;
      setSessionTitle(paperTitle || 'Enhanced Research');
      setBrainDump(paperContent || '');
      setShowUploadOption(false);
      setShowTitleInput(false);
      
      // Trigger document analysis
      if (paperContent) {
        handleEvaluationImport(paperContent, paperTitle);
      }
    }
  }, [location.state]);

  const handleEvaluationImport = async (content, title) => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/analyze-document`, {
        content: content,
        title: title
      });

      if (response.data.success !== false) {
        setDocAnalysis(response.data);
        setCustomPrompts({
          spark: response.data.spark_prompts || [],
          mirror: response.data.mirror_prompts || []
        });
        
        toast({
          title: "Paper Imported!",
          description: "Custom prompts generated. Ready to enhance your work.",
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Using Default Prompts",
        description: "Starting with standard OES prompts",
        variant: "default"
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const currentStage = stages[currentStageIndex];
  const activePersonas = getActivePersonas();
  const activePersonasData = getAllActivePersonasData();

  const getPromptsForStage = () => {
    // Priority 1: Use contextual AI-generated questions if available
    if (contextualQuestions && contextualQuestions[currentStage.key]) {
      return contextualQuestions[currentStage.key];
    }
    
    // Priority 2: Use document-based custom prompts if available
    if (customPrompts && currentStage.key === 'spark' && customPrompts.spark.length > 0) {
      return customPrompts.spark;
    }
    if (customPrompts && currentStage.key === 'mirror' && customPrompts.mirror.length > 0) {
      return customPrompts.mirror;
    }
    
    // Priority 3: Use challenge-specific prompts if available
    if (challengePrompts && challengePrompts[currentStage.key]) {
      return challengePrompts[currentStage.key];
    }
    
    // Priority 4: Use persona-specific prompts
    if (activePersonas.length > 0 && (currentStage.key === 'spark' || currentStage.key === 'mirror')) {
      // Get persona-specific prompts
      let allPrompts = [];
      activePersonas.forEach(personaId => {
        const personaPrompts = getPersonaPrompts(personaId, currentStage.key === 'spark' ? 'sparkActivation' : 'mirrorValidation');
        allPrompts = allPrompts.concat(personaPrompts);
      });
      return allPrompts.slice(0, currentStage.prompts);
    }
    
    // Priority 5: Default prompts
    const defaultPrompts = {
      spark: [
        "What problem are you trying to solve with this idea?",
        "What personal experience or observation led you to this idea?",
        "If this works perfectly, what does success look like to you?"
      ],
      mirror: [
        "What existing solutions or approaches currently exist?",
        "What research or evidence supports the need for this?",
        "What challenges have others faced trying to solve this?"
      ],
      iteration: [
        "Based on what you've explored, how would you refine your idea?",
        "What's one thing you'd adopt from existing solutions and one you'd do differently?"
      ]
    };
    
    return defaultPrompts[currentStage.key] || [];
  };
  
  // Generate contextual questions when title is set
  const generateContextualQuestions = async (title) => {
    if (!title || title.length < 5) return;
    
    setGeneratingQuestions(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/generate-contextual-questions`, {
        idea_title: title,
        personas: activePersonas.length > 0 ? activePersonas : ['student']
      });
      
      if (response.data.success && response.data.questions) {
        setContextualQuestions(response.data.questions);
        toast({
          title: "✨ Questions Personalized",
          description: `Tailored prompts generated for "${title.substring(0, 30)}..."`,
        });
      } else if (response.data.questions) {
        // Fallback questions returned
        setContextualQuestions(response.data.questions);
      }
    } catch (error) {
      console.error('Error generating contextual questions:', error);
      // Silent fail - will use default prompts
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const handleAnswerChange = (index, value) => {
    setAnswers(prev => ({
      ...prev,
      [currentStage.key]: {
        ...prev[currentStage.key],
        [index]: value
      }
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const validExtensions = ['.txt', '.pdf', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a TXT, PDF, or DOCX file",
        variant: "destructive"
      });
      return;
    }

    // Check file size (25MB limit)
    if (file.size > 25 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please upload a file smaller than 25MB",
        variant: "destructive"
      });
      return;
    }

    setAnalyzing(true);

    try {
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Call backend to analyze
      const response = await axios.post(`${BACKEND_URL}/api/upload-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success !== false) {
        setDocAnalysis(response.data);
        setSessionTitle(response.data.title_suggestion || file.name.replace(/\.[^/.]+$/, ''));
        setCustomPrompts({
          spark: response.data.spark_prompts || [],
          mirror: response.data.mirror_prompts || []
        });
        setUploadedDoc(file.name);
        
        toast({
          title: "Document Analyzed!",
          description: "Custom prompts generated based on your paper",
        });
      } else {
        throw new Error(response.data.error || "Analysis failed");
      }
      
      setAnalyzing(false);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to analyze document. Please try again.",
        variant: "destructive"
      });
      setAnalyzing(false);
    }
  };

  const isStageComplete = () => {
    const stageAnswers = answers[currentStage.key] || {};
    const prompts = getPromptsForStage();
    return Object.keys(stageAnswers).length >= prompts.length;
  };

  const handleNext = async () => {
    if (!isStageComplete()) {
      toast({
        title: "Complete all prompts",
        description: "Please answer all questions before proceeding",
        variant: "destructive"
      });
      return;
    }

    if (currentStageIndex < stages.length - 1) {
      setCurrentStageIndex(currentStageIndex + 1);
    } else {
      // All stages complete - analyze with AI
      await analyzeIdea();
    }
  };

  const analyzeIdea = async () => {
    setAnalyzing(true);
    
    try {
      // Compile description from answers
      const sparkAnswers = answers.spark || {};
      const mirrorAnswers = answers.mirror || {};
      const description = [
        ...Object.values(sparkAnswers),
        ...Object.values(mirrorAnswers)
      ].join(' ');
      
      // Step 1: Get OI/MSA Analysis
      toast({
        title: "🔍 Analyzing Your Idea...",
        description: "Running originality assessment"
      });
      
      const response = await axios.post(`${BACKEND_URL}/api/analyze-idea`, {
        title: sessionTitle,
        description: description,
        stage_answers: answers
      });
      
      setAnalysisResults(response.data);
      
      // Step 2: Save session with OI/MSA scores
      let savedSessionId = null;
      try {
        toast({
          title: "💾 Saving Session...",
          description: "Preserving your idea and scores"
        });
        
        const sessionData = {
          title: sessionTitle,
          content: description,
          persona: getActivePersonas().join(', '),
          stage_answers: answers,
          oi_scores: response.data.oi,
          msa_scores: response.data.msa,
          status: 'completed'
        };
        
        // Get auth token
        const token = localStorage.getItem('if_auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const saveResponse = await axios.post(`${BACKEND_URL}/api/sessions`, sessionData, {
          headers,
          withCredentials: true
        });
        
        savedSessionId = saveResponse.data.id;
        setSessionId(savedSessionId);
        
        // Track guest session for later claiming if user is not authenticated
        if (!isAuthenticated && savedSessionId) {
          trackGuestSession(savedSessionId);
        }
        
        // Step 3: Auto-generate structured report
        toast({
          title: "📝 Generating Report...",
          description: "Creating your idea summary"
        });
        
        try {
          const reportResponse = await axios.post(
            `${BACKEND_URL}/api/sessions/${savedSessionId}/generate-report`,
            {},
            { headers, withCredentials: true }
          );
          
          if (reportResponse.data.success) {
            setStructuredReport(reportResponse.data.report);
            
            toast({
              title: "✅ Complete!",
              description: "Your IF Report is ready to download",
              duration: 5000
            });
          }
        } catch (reportError) {
          console.error('Auto-report generation error:', reportError);
          toast({
            title: "Analysis Complete!",
            description: "Report generation failed - you can retry later",
            variant: "destructive"
          });
        }
        
      } catch (saveError) {
        console.error('Auto-save error:', saveError);
        toast({
          title: "Analysis Complete!",
          description: "Session save failed - scores shown but not persisted",
          variant: "destructive"
        });
      }
      
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
      
      // Fallback scores
      setAnalysisResults({
        oi: {
          total: 7.0,
          novelty: 7,
          resonance: 7,
          synthesis: 7,
          ethical: 7,
          novelty_reasoning: "Moderate novelty detected",
          resonance_reasoning: "Good market fit",
          synthesis_reasoning: "Good concept integration",
          ethical_reasoning: "Ethically sound",
          similar_precedents: [],
          key_innovations: []
        },
        msa: {
          mirror_pct: 45,
          spark_pct: 55,
          assessment: "Strong Spark"
        }
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    try {
      // Get auth token
      const token = localStorage.getItem('if_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Save session to database
      const sessionData = {
        title: sessionTitle,
        content: Object.values(answers).map(stageAnswers => 
          Object.values(stageAnswers).join(' ')
        ).join('\n'),
        persona: getActivePersonas().join(', '),
        stage_answers: answers,
        oi_scores: analysisResults?.oi || null,
        msa_scores: analysisResults?.msa || null,
        status: analysisResults ? 'completed' : 'in-progress',
        impact_category: impactCategory || null
      };

      const response = await axios.post(`${BACKEND_URL}/api/sessions`, sessionData, {
        headers,
        withCredentials: true
      });

      const savedSession = response.data;
      setSessionId(savedSession.id);

      // Track guest session for later claiming if user is not authenticated
      if (!isAuthenticated && savedSession.id) {
        trackGuestSession(savedSession.id);
      }

      if (challengeId) {
        setShowSubmissionModal(true);
      } else {
        toast({
          title: "Session Saved!",
          description: `Your thinking session "${sessionTitle}" has been saved to your account.`
        });
        setTimeout(() => navigate('/sessions'), 1500);
      }
      
      return savedSession;
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Save Failed",
        description: error.response?.data?.detail || "Failed to save session. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  // Generate Structured Idea Report
  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      
      // Make sure session is saved first
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const savedSession = await handleSave();
        if (!savedSession) {
          setGeneratingReport(false);
          return;
        }
        currentSessionId = savedSession.id;
      }
      
      // Get auth token
      const token = localStorage.getItem('if_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${BACKEND_URL}/api/sessions/${currentSessionId}/generate-report`,
        {},
        { headers, withCredentials: true }
      );

      if (response.data.success) {
        setStructuredReport(response.data.report);
        toast({
          title: "Report Generated!",
          description: "Your structured idea report is ready."
        });
      } else {
        throw new Error(response.data.error || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Report generation error:', error);
      toast({
        title: "Report Generation Failed",
        description: error.response?.data?.detail || "Failed to generate report. Please ensure you've completed the session prompts.",
        variant: "destructive"
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  // Download PDF Report
  const handleDownloadReportPDF = async () => {
    try {
      setDownloadingPDF(true);
      
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const savedSession = await handleSave();
        if (!savedSession) {
          setDownloadingPDF(false);
          return;
        }
        currentSessionId = savedSession.id;
      }

      // Use axios with proper auth headers for PDF download
      const pdfUrl = `${BACKEND_URL}/api/sessions/${currentSessionId}/report-pdf`;
      
      const response = await axios.get(pdfUrl, {
        responseType: 'blob',
        withCredentials: true
      });
      
      // Create download link from blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IF_Report_${sessionTitle.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "PDF Downloaded!",
        description: "Your idea report has been downloaded."
      });
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: "Download Failed",
        description: error.response?.data?.detail || "Failed to download PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Generate Institute Proposal (IRB, Academic, etc.)
  const handleGenerateProposal = async (proposalType = 'irb') => {
    try {
      setGeneratingProposal(true);
      setSelectedProposalType(proposalType);
      
      // Ensure session is saved
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const savedSession = await handleSave();
        if (!savedSession) {
          toast({
            title: "Save Required",
            description: "Please save your session first",
            variant: "destructive"
          });
          return;
        }
        currentSessionId = savedSession.id;
      }
      
      // Get auth token
      const token = localStorage.getItem('if_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Generate proposal
      const response = await axios.post(
        `${BACKEND_URL}/api/sessions/${currentSessionId}/generate-proposal`,
        { proposal_type: proposalType },
        { headers, withCredentials: true }
      );
      
      if (response.data.success) {
        setProposalData(response.data);
        setShowProposalModal(true);
        toast({
          title: "Proposal Generated!",
          description: "Your institute proposal is ready for review"
        });
      } else {
        throw new Error(response.data.error || "Failed to generate proposal");
      }
    } catch (error) {
      console.error('Proposal generation error:', error);
      toast({
        title: "Generation Failed",
        description: error.response?.data?.detail || error.message || "Failed to generate proposal",
        variant: "destructive"
      });
    } finally {
      setGeneratingProposal(false);
    }
  };

  // Download Proposal PDF
  const handleDownloadProposalPDF = async (proposalType = 'irb') => {
    try {
      setGeneratingProposal(true);
      
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        toast({
          title: "Save Required",
          description: "Please save your session first",
          variant: "destructive"
        });
        return;
      }
      
      const pdfUrl = `${BACKEND_URL}/api/sessions/${currentSessionId}/proposal/${proposalType}/pdf`;
      
      const response = await fetch(pdfUrl, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download proposal PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const typeLabels = {
        irb: 'IRB_Approval',
        academic: 'Academic_Proposal',
        incubator: 'Startup_Pitch',
        grant: 'Grant_Application'
      };
      const typeLabel = typeLabels[proposalType] || 'Proposal';
      a.download = `${typeLabel}_${sessionTitle?.substring(0, 30).replace(/\s+/g, '_') || 'proposal'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Download Complete",
        description: "Your proposal has been downloaded"
      });
    } catch (error) {
      console.error('Proposal PDF download error:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download proposal PDF",
        variant: "destructive"
      });
    } finally {
      setGeneratingProposal(false);
    }
  };

  // Navigate to Evaluate Paper with compiled content
  const handleEvaluateAsReport = async () => {
    try {
      // Ensure session is saved and report is generated
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const savedSession = await handleSave();
        if (!savedSession) return;
        currentSessionId = savedSession.id;
      }

      // Generate report if not already done
      if (!structuredReport) {
        await handleGenerateReport();
      }

      // Compile all content for evaluation
      const compiledContent = structuredReport ? `
# ${structuredReport.title || sessionTitle}

## Executive Summary
${structuredReport.executive_summary || ''}

## Problem Statement
${structuredReport.problem_statement || ''}

## Why It Matters
${structuredReport.why_it_matters || ''}

## Existing Solutions & Precedents
${structuredReport.existing_solutions || ''}

## Proposed Solution
${structuredReport.proposed_solution || ''}

## Key Innovations
${(structuredReport.key_innovations || []).map((i, idx) => `${idx + 1}. ${i}`).join('\n')}

## Potential Challenges
${(structuredReport.potential_challenges || []).map((c, idx) => `${idx + 1}. ${c}`).join('\n')}

## Anticipated Impact
${structuredReport.anticipated_impact || ''}

## Next Steps
${(structuredReport.next_steps || []).map((s, idx) => `${idx + 1}. ${s}`).join('\n')}
`.trim() : Object.values(answers).map(stageAnswers => 
        Object.values(stageAnswers).join('\n')
      ).join('\n\n');

      // Navigate to Quick Evaluate with pre-filled content
      navigate('/quick-evaluate', { 
        state: { 
          prefilledContent: compiledContent,
          prefilledTitle: structuredReport?.title || sessionTitle,
          fromSession: currentSessionId
        } 
      });

    } catch (error) {
      console.error('Navigate to evaluate error:', error);
      toast({
        title: "Error",
        description: "Failed to prepare content for evaluation.",
        variant: "destructive"
      });
    }
  };

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [sessionId, setSessionId] = useState(null);
  const autoSaveTimeoutRef = useRef(null);

  // Auto-save function with debouncing
  const autoSave = useCallback(async () => {
    // Don't auto-save if title is empty or no answers yet
    if (!sessionTitle.trim() || Object.keys(answers).length === 0) {
      return;
    }

    try {
      setAutoSaveStatus('saving');
      
      // Get auth token
      const token = localStorage.getItem('if_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const sessionData = {
        title: sessionTitle,
        content: Object.values(answers).map(stageAnswers => 
          Object.values(stageAnswers).join(' ')
        ).join('\n'),
        persona: getActivePersonas().join(', '),
        stage_answers: answers,
        oi_scores: analysisResults?.oi || null,
        msa_scores: analysisResults?.msa || null,
        status: analysisResults ? 'completed' : 'in-progress',
        impact_category: impactCategory || null
      };

      // Update existing session or create new one
      if (sessionId) {
        await axios.put(`${BACKEND_URL}/api/sessions/${sessionId}`, sessionData, {
          headers,
          withCredentials: true
        });
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/sessions`, sessionData, {
          headers,
          withCredentials: true
        });
        const newSessionId = response.data.id;
        setSessionId(newSessionId);
        
        // Track guest session for later claiming if user is not authenticated
        if (!isAuthenticated && newSessionId) {
          trackGuestSession(newSessionId);
        }
      }

      setAutoSaveStatus('saved');
      
      // Clear "saved" status after 2 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save error:', error);
      
      // Check for quota exceeded error
      if (error.response?.status === 403 && error.response?.data?.detail?.quota_exceeded) {
        const detail = error.response.data.detail;
        setAutoSaveStatus('quota');
        toast({
          title: "Session Limit Reached",
          description: `You've used ${detail.usage}/${detail.limit} free sessions this month. Visit /pricing to upgrade for more sessions.`,
          variant: "destructive"
        });
      } else {
        setAutoSaveStatus('error');
      }
      setTimeout(() => setAutoSaveStatus('idle'), 5000);
    }
  }, [sessionTitle, answers, getActivePersonas, analysisResults, sessionId, impactCategory, isAuthenticated, BACKEND_URL]);

  // Debounced auto-save effect
  useEffect(() => {
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 2000);

    // Cleanup
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [sessionTitle, answers, getActivePersonas, autoSave]);

  // Auto-save on page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (sessionTitle.trim() && Object.keys(answers).length > 0 && autoSaveStatus !== 'saved') {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionTitle, answers, autoSaveStatus]);

  const getOILabel = (score) => {
    if (score >= 9) return 'Exceptional';
    if (score >= 8) return 'Very High';
    if (score >= 6) return 'Moderate';
    return 'Developing';
  };

  const progress = ((currentStageIndex + 1) / stages.length) * 100;
  const prompts = getPromptsForStage();

  // Show loading while fetching existing session
  if (loadingSession) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-violet-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (showUploadOption) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-3xl text-center mb-2">Start Your IF Session</CardTitle>
            <p className="text-center text-slate-600">Choose how you&apos;d like to begin</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Option 1: Brand New Session */}
            <Card className="border-2 border-purple-200 hover:border-purple-400 transition-all cursor-pointer"
                  onClick={() => { setShowUploadOption(false); setShowTitleInput(true); }}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Start Brand New</h3>
                    <p className="text-slate-600">Begin with a fresh idea. Go through guided thinking stages to develop your concept from scratch.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Option 2: Upload Existing Work */}
            <Card className="border-2 border-emerald-200 hover:border-emerald-400 transition-all cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Upload Existing Paper/Work</h3>
                    <p className="text-slate-600 mb-4">Already have a draft or research paper? Upload it to get AI-powered analysis and custom prompts targeting weak areas.</p>
                    
                    <label htmlFor="file-upload">
                      <div className="border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-all cursor-pointer">
                        {analyzing ? (
                          <>
                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-600 mb-2" />
                            <p className="text-sm text-slate-600">Analyzing document...</p>
                          </>
                        ) : uploadedDoc ? (
                          <>
                            <CheckCircle2 className="w-8 h-8 mx-auto text-green-600 mb-2" />
                            <p className="text-sm font-semibold text-green-700">{uploadedDoc}</p>
                            <p className="text-xs text-slate-500 mt-1">Document analyzed! Custom prompts ready.</p>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center justify-center gap-2 mb-2">
                              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                            </div>
                            <p className="text-sm font-medium text-slate-700">Click to upload or drag & drop</p>
                            <p className="text-xs text-slate-500 mt-1">PDF, DOCX, or TXT (max 25MB)</p>
                          </>
                        )}
                      </div>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={analyzing}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {uploadedDoc && docAnalysis && (
              <div className="space-y-4">
                {/* Show abstract */}
                {docAnalysis.abstract && (
                  <Card className="border-emerald-200">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-sm text-emerald-900 mb-2">Document Summary</h4>
                      <p className="text-sm text-slate-700">{docAnalysis.abstract}</p>
                    </CardContent>
                  </Card>
                )}
                
                <Button
                  className="w-full bg-gradient-to-r from-emerald-600 via-cyan-600 to-blue-600"
                  onClick={() => { setShowUploadOption(false); setShowTitleInput(false); }}
                  size="lg"
                >
                  Continue with Custom Prompts
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showTitleInput) {
    // Special Challenge Mode UI
    if (challengeId && challengeData) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-6">
          <Card className="w-full max-w-3xl border-2 border-emerald-200">
            <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-6 h-6" />
                <Badge className="bg-white/20 text-white text-xs">Challenge Mode</Badge>
              </div>
              <CardTitle className="text-2xl">
                {challengeData.title}
              </CardTitle>
              <p className="text-emerald-100 mt-2 text-sm">
                {challengeData.description}
              </p>
              {challengeData.sdg_name && (
                <Badge className="mt-3 bg-white/20 text-white">
                  🌍 {challengeData.sdg_name}
                </Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {/* Challenge Tags */}
              {challengeData.tags && challengeData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {challengeData.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-emerald-700 border-emerald-300">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Loading Questions */}
              {generatingQuestions && (
                <div className="flex items-center justify-center gap-3 py-8 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                  <span className="text-emerald-700 font-medium">
                    Generating personalized questions for this challenge...
                  </span>
                </div>
              )}
              
              {/* Ready to Start */}
              {!generatingQuestions && (
                <>
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      What you'll explore:
                    </h4>
                    <ul className="text-sm text-emerald-700 space-y-1">
                      <li>• <strong>Spark:</strong> Generate innovative solutions for this challenge</li>
                      <li>• <strong>Mirror:</strong> Research existing approaches and stakeholders</li>
                      <li>• <strong>Iterate:</strong> Refine and strengthen your solution</li>
                    </ul>
                  </div>
                  
                  {/* Quick Brain Dump for Challenge */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Initial thoughts on this challenge (optional):
                    </label>
                    <Textarea
                      placeholder={`What's your initial reaction to "${challengeData.title}"? Any ideas already forming?`}
                      value={brainDump}
                      onChange={(e) => setBrainDump(e.target.value)}
                      className="min-h-[120px] text-base resize-none border-emerald-200 focus:border-emerald-400"
                    />
                  </div>
                </>
              )}
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/challenges')}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Challenges
                </Button>
                <Button
                  onClick={() => {
                    if (brainDump.trim()) {
                      // Extract title from brain dump
                      const words = brainDump.split(' ').slice(0, 8).join(' ');
                      const title = words.length > 50 ? words.substring(0, 50) + '...' : words;
                      setSessionTitle(`Solution: ${title}`);
                    }
                    setShowTitleInput(false);
                  }}
                  disabled={generatingQuestions}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                >
                  {generatingQuestions ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Preparing...
                    </>
                  ) : (
                    <>
                      Start Solving <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    // Normal Mode UI
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-3xl text-center bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
              {introPrompt}
            </CardTitle>
            <p className="text-center text-slate-600 mt-2">
              Share your thoughts, questions, or ideas - no structure needed
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Persona Selector - Choose before starting */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-slate-700">Your Thinking Style</span>
                </div>
                {activePersonasData.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {activePersonasData.map(persona => (
                      <Badge key={persona.id} variant="secondary" className="text-xs">
                        {persona.icon} {persona.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {personas.map(persona => {
                  const isActive = activePersonas.includes(persona.id);
                  return (
                    <button
                      key={persona.id}
                      onClick={() => togglePersona(persona.id)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        isActive
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-purple-300'
                      }`}
                    >
                      {persona.icon} {persona.name}
                    </button>
                  );
                })}
              </div>
              
              {/* Show combined focus metrics for ALL selected personas */}
              {activePersonasData.length > 0 && (
                <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-700 font-medium mb-2">
                    Focus Metrics for this session 
                    {activePersonasData.length > 1 && (
                      <span className="text-purple-500 ml-1">(combined from {activePersonasData.length} personas)</span>
                    )}:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {/* Collect all unique metrics from all selected personas */}
                    {[...new Set(activePersonasData.flatMap(p => p.metrics || []))].map((metric, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 bg-white rounded border border-purple-200 text-purple-700">
                        📊 {metric}
                      </span>
                    ))}
                  </div>
                  {activePersonasData.length > 1 && (
                    <p className="text-xs text-purple-600 mt-2 italic">
                      Questions will blend perspectives from: {activePersonasData.map(p => p.name).join(' + ')}
                    </p>
                  )}
                </div>
              )}
              
              <p className="text-xs text-slate-500 mt-2">
                Questions will be tailored to your perspective and focus metrics
              </p>
            </div>

            <Textarea
              placeholder="Start typing... What's on your mind? What problem are you thinking about? What sparked your curiosity?"
              value={brainDump}
              onChange={(e) => setBrainDump(e.target.value)}
              className="min-h-[200px] text-lg resize-none"
              autoFocus
            />
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  // Refresh with a new random prompt
                  const activePersonas = getAllActivePersonasData();
                  const prompt = getIntroPrompt(activePersonas);
                  setIntroPrompt(prompt);
                }}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Try Another Prompt
              </Button>
              
              <Button
                className="flex-1 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
                onClick={() => {
                  if (!brainDump.trim()) {
                    toast({
                      title: "Share Your Thoughts",
                      description: "Tell us what's on your mind to get started",
                      variant: "destructive"
                    });
                    return;
                  }
                  
                  // Extract title from first line or use first few words
                  const firstLine = brainDump.trim().split('\n')[0];
                  const extractedTitle = firstLine.length > 60 
                    ? firstLine.substring(0, 60) + '...' 
                    : firstLine;
                  setSessionTitle(extractedTitle || 'Untitled Idea');
                  
                  // Store brain dump as initial context (can be used later)
                  setAnswers(prev => ({
                    ...prev,
                    initialContext: brainDump
                  }));
                  
                  // Track session start
                  trackSessionStart(extractedTitle);
                  
                  // Generate contextual questions based on the idea title
                  generateContextualQuestions(extractedTitle);
                  
                  setShowTitleInput(false);
                }}
              >
                <Zap className="w-4 h-4 mr-2" />
                Start Thinking
              </Button>
            </div>
            
            <p className="text-sm text-slate-500 text-center">
              💡 Tip: The more context you share, the better AI can guide your ideation
            </p>
            
            {/* Impact Category Selector (Optional) */}
            <div className="pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-slate-700">What impact area does your idea address? (Optional)</span>
              </div>
              <ImpactCategorySelector
                value={impactCategory}
                onChange={(cat) => setImpactCategory(cat)}
                mode="single"
                showDescription={true}
                collapsible={true}
                placeholder="Search impact areas..."
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (analyzing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <Loader2 className="w-16 h-16 animate-spin mx-auto text-purple-600 mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Analyzing Your Idea...</h3>
            <p className="text-slate-600">AI is evaluating originality, resonance, and impact</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (analysisResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Brain className="w-8 h-8 text-purple-600" />
              <h1 className="text-xl font-bold">{sessionTitle}</h1>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-6 py-12 max-w-6xl">
          {/* Report Ready Banner */}
          <Card className="mb-8 border-2 border-purple-300 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white overflow-hidden">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Your IF Report is Ready!</h2>
                    <p className="text-white/90">Complete analysis with OI Score: {analysisResults.oi?.total || 'N/A'}/10</p>
                  </div>
                </div>
                <Button 
                  onClick={handleDownloadReportPDF}
                  disabled={downloadingPDF || !sessionId}
                  size="lg"
                  className="bg-white text-purple-700 hover:bg-purple-50 font-bold px-8"
                >
                  {downloadingPDF ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Preparing...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Download Full Report (PDF)
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Idea Summary Card - Show if structured report is available */}
          {structuredReport && (
            <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-xl text-purple-900">
                  <FileText className="w-6 h-6" />
                  Your Idea Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Executive Summary */}
                {structuredReport.executive_summary && (
                  <div className="p-4 bg-white/80 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-900 mb-2 text-sm uppercase tracking-wide">Executive Summary</h4>
                    <p className="text-slate-700">{structuredReport.executive_summary}</p>
                  </div>
                )}
                
                {/* Problem & Solution Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  {structuredReport.problem_statement && (
                    <div className="p-4 bg-white/80 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-900 mb-2 text-sm uppercase tracking-wide">Problem Statement</h4>
                      <p className="text-sm text-slate-700">{structuredReport.problem_statement}</p>
                    </div>
                  )}
                  {structuredReport.proposed_solution && (
                    <div className="p-4 bg-white/80 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-2 text-sm uppercase tracking-wide">Proposed Solution</h4>
                      <p className="text-sm text-slate-700">{structuredReport.proposed_solution}</p>
                    </div>
                  )}
                </div>

                {/* Why It Matters & Impact */}
                <div className="grid md:grid-cols-2 gap-4">
                  {structuredReport.why_it_matters && (
                    <div className="p-4 bg-white/80 rounded-lg border border-pink-200">
                      <h4 className="font-semibold text-pink-900 mb-2 text-sm uppercase tracking-wide">Why It Matters</h4>
                      <p className="text-sm text-slate-700">{structuredReport.why_it_matters}</p>
                    </div>
                  )}
                  {structuredReport.anticipated_impact && (
                    <div className="p-4 bg-white/80 rounded-lg border border-amber-200">
                      <h4 className="font-semibold text-amber-900 mb-2 text-sm uppercase tracking-wide">Anticipated Impact</h4>
                      <p className="text-sm text-slate-700">{structuredReport.anticipated_impact}</p>
                    </div>
                  )}
                </div>

                {/* Key Innovations as chips */}
                {structuredReport.key_innovations?.length > 0 && (
                  <div className="p-4 bg-white/80 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-900 mb-3 text-sm uppercase tracking-wide">Key Innovations</h4>
                    <div className="flex flex-wrap gap-2">
                      {structuredReport.key_innovations.map((innovation, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm">
                          <Sparkles className="w-3 h-3" />
                          {innovation.length > 60 ? innovation.substring(0, 60) + '...' : innovation}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strengths & Areas for Improvement */}
                <div className="grid md:grid-cols-2 gap-4">
                  {structuredReport.key_strengths?.length > 0 && (
                    <div className="p-4 bg-white/80 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2 text-sm uppercase tracking-wide">Key Strengths</h4>
                      <ul className="space-y-1">
                        {structuredReport.key_strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{strength}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {structuredReport.improvement_areas?.length > 0 && (
                    <div className="p-4 bg-white/80 rounded-lg border border-orange-200">
                      <h4 className="font-semibold text-orange-900 mb-2 text-sm uppercase tracking-wide">Areas for Improvement</h4>
                      <ul className="space-y-1">
                        {structuredReport.improvement_areas.map((area, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                            <span>{area}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Similar Precedents */}
                {structuredReport.similar_precedents?.length > 0 && (
                  <div className="p-4 bg-white/80 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold text-indigo-900 mb-2 text-sm uppercase tracking-wide">Similar Precedents</h4>
                    <ul className="space-y-1">
                      {structuredReport.similar_precedents.map((precedent, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <Building className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                          <span>{precedent}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Institute Proposal Generator Card */}
          {sessionId && (
            <Card className="mb-6 border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-xl text-indigo-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  Generate Institute Proposal
                </CardTitle>
                <p className="text-sm text-slate-600 mt-1">
                  Transform your idea into a formal proposal for institute submission
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Proposal Types - All Enabled */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* IRB Approval - For Medical Research */}
                  <button
                    onClick={() => handleGenerateProposal('irb')}
                    disabled={generatingProposal}
                    className="group p-4 bg-white rounded-xl border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-lg transition-all text-left"
                    data-testid="proposal-irb-btn"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck className="w-5 h-5 text-indigo-600" />
                      <span className="font-semibold text-indigo-900">IRB Approval</span>
                    </div>
                    <p className="text-xs text-slate-600">Medical/Clinical research ethics approval</p>
                    <Badge className="mt-2 bg-indigo-100 text-indigo-700 text-xs">Medical Research</Badge>
                  </button>
                  
                  {/* Academic Proposal - Now Enabled */}
                  <button
                    onClick={() => handleGenerateProposal('academic')}
                    disabled={generatingProposal}
                    className="group p-4 bg-white rounded-xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all text-left"
                    data-testid="proposal-academic-btn"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Building className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-900">Academic</span>
                    </div>
                    <p className="text-xs text-slate-600">University research proposal</p>
                    <Badge className="mt-2 bg-blue-100 text-blue-700 text-xs">Universities</Badge>
                  </button>
                  
                  {/* Grant Application - Now Enabled */}
                  <button
                    onClick={() => handleGenerateProposal('grant')}
                    disabled={generatingProposal}
                    className="group p-4 bg-white rounded-xl border-2 border-green-200 hover:border-green-400 hover:shadow-lg transition-all text-left"
                    data-testid="proposal-grant-btn"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-green-900">Grant</span>
                    </div>
                    <p className="text-xs text-slate-600">Research funding application</p>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Funding</Badge>
                  </button>
                  
                  {/* Incubator Pitch - Now Enabled */}
                  <button
                    onClick={() => handleGenerateProposal('incubator')}
                    disabled={generatingProposal}
                    className="group p-4 bg-white rounded-xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-lg transition-all text-left"
                    data-testid="proposal-incubator-btn"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-orange-600" />
                      <span className="font-semibold text-orange-900">Startup</span>
                    </div>
                    <p className="text-xs text-slate-600">Startup pitch for incubators</p>
                    <Badge className="mt-2 bg-orange-100 text-orange-700 text-xs">Startups</Badge>
                  </button>
                </div>
                
                {generatingProposal && (
                  <div className="flex items-center justify-center gap-3 py-4 bg-indigo-50 rounded-lg">
                    <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                    <span className="text-indigo-700 font-medium">Generating your proposal... This may take a minute.</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Proposal Preview Modal */}
          {showProposalModal && proposalData && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <GraduationCap className="w-6 h-6" />
                      {proposalData.proposal_type_name || 'IRB Proposal'}
                    </CardTitle>
                    <button onClick={() => setShowProposalModal(false)} className="p-1 hover:bg-white/20 rounded">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-indigo-100 text-sm mt-1">{proposalData.title}</p>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-6">
                  {/* OI Scores Summary */}
                  {proposalData.oi_scores && (
                    <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
                      <h4 className="font-semibold text-indigo-900 mb-2">Originality Assessment</h4>
                      <div className="grid grid-cols-5 gap-2 text-center text-sm">
                        <div><span className="font-bold text-indigo-700">{proposalData.oi_scores.novelty || '-'}</span><br/><span className="text-xs text-slate-600">Novelty</span></div>
                        <div><span className="font-bold text-indigo-700">{proposalData.oi_scores.resonance || '-'}</span><br/><span className="text-xs text-slate-600">Resonance</span></div>
                        <div><span className="font-bold text-indigo-700">{proposalData.oi_scores.synthesis || '-'}</span><br/><span className="text-xs text-slate-600">Synthesis</span></div>
                        <div><span className="font-bold text-indigo-700">{proposalData.oi_scores.ethical || proposalData.oi_scores.ethical_anchoring || '-'}</span><br/><span className="text-xs text-slate-600">Ethical</span></div>
                        <div><span className="font-bold text-purple-700 text-lg">{proposalData.oi_scores.total || proposalData.oi_scores.total_oi || '-'}/10</span><br/><span className="text-xs text-slate-600">Overall OI</span></div>
                      </div>
                    </div>
                  )}
                  
                  {/* Proposal Sections Preview */}
                  <div className="space-y-4">
                    {proposalData.sections && typeof proposalData.sections === 'object' && !proposalData.sections.parse_error ? (
                      Object.entries(proposalData.sections).map(([key, section]) => (
                        <div key={key} className="border-b border-slate-200 pb-4 last:border-0">
                          <h4 className="font-semibold text-slate-900 mb-2">
                            {typeof section === 'object' ? section.title || key.replace(/_/g, ' ').toUpperCase() : key.replace(/_/g, ' ').toUpperCase()}
                          </h4>
                          <div className="text-sm text-slate-700 whitespace-pre-wrap">
                            {typeof section === 'object' ? (
                              section.content?.substring(0, 500) + (section.content?.length > 500 ? '...' : '')
                            ) : (
                              String(section).substring(0, 500) + (String(section).length > 500 ? '...' : '')
                            )}
                          </div>
                        </div>
                      ))
                    ) : proposalData.sections?.raw_content ? (
                      <div className="text-sm text-slate-700 whitespace-pre-wrap">
                        {proposalData.sections.raw_content.substring(0, 2000)}...
                      </div>
                    ) : (
                      <p className="text-slate-600">Proposal content preview not available. Download PDF for full document.</p>
                    )}
                  </div>
                </CardContent>
                <div className="flex-shrink-0 border-t p-4 bg-slate-50 flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowProposalModal(false)}>
                    Close
                  </Button>
                  <Button 
                    onClick={() => handleDownloadProposalPDF(selectedProposalType)}
                    disabled={generatingProposal}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {generatingProposal ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download PDF</>
                    )}
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Loading state for summary generation - only show when actively generating */}
          {!structuredReport && sessionId && generatingReport && (
            <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardContent className="py-8 text-center">
                <Loader2 className="w-8 h-8 mx-auto text-purple-600 animate-spin mb-3" />
                <p className="text-purple-700 font-medium">Generating your idea summary...</p>
                <p className="text-sm text-blue-600 mt-1">This will take a few seconds</p>
              </CardContent>
            </Card>
          )}

          {/* Generate Report button when report doesn't exist but session is saved */}
          {!structuredReport && sessionId && !generatingReport && (
            <Card className="mb-6 border-2 border-slate-200 bg-slate-50">
              <CardContent className="py-6 text-center">
                <p className="text-slate-600 mb-3">Generate a detailed summary of your session</p>
                <Button
                  onClick={() => handleGenerateReport()}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="generate-report-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          )}

          {/* OI Score Card */}
          <Card className="mb-6 border-2 border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Trophy className="w-7 h-7 text-purple-600" />
                    Originality Index: {analysisResults.oi.total}/10
                  </CardTitle>
                  <p className="text-sm text-slate-600 mt-1">{analysisResults.oi.formula}</p>
                </div>
                <Badge className="text-lg px-4 py-2 bg-purple-600 text-white">
                  {analysisResults.oi.interpretation}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Overall Interpretation */}
              {analysisResults.oi.interpretation && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Overall Assessment
                  </h4>
                  <p className="text-blue-800">{analysisResults.oi.interpretation}</p>
                </div>
              )}

              {/* Dimension Scores */}
              <div className="space-y-6">
                {/* Novelty */}
                <div className="p-5 bg-purple-50 rounded-lg border-2 border-purple-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-purple-900">Novelty (Weight: 30%)</span>
                      <p className="text-xs text-purple-700 mt-1">How unprecedented and unique is this?</p>
                    </div>
                    <Badge className="bg-purple-600 text-white text-lg px-4 py-2">
                      {analysisResults.oi.novelty}/10
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-700 mb-2">{analysisResults.oi.novelty_reasoning}</p>
                  {analysisResults.oi.novelty_justification && (
                    <div className="mt-2 p-3 bg-white rounded border border-purple-200">
                      <p className="text-xs font-semibold text-purple-900">Why this score?</p>
                      <p className="text-xs text-slate-600 mt-1">{analysisResults.oi.novelty_justification}</p>
                    </div>
                  )}
                </div>

                {/* Resonance */}
                <div className="p-5 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-blue-900">Resonance (Weight: 30%)</span>
                      <p className="text-xs text-blue-700 mt-1">How well does it address real needs?</p>
                    </div>
                    <Badge className="bg-blue-600 text-white text-lg px-4 py-2">
                      {analysisResults.oi.resonance}/10
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-700 mb-2">{analysisResults.oi.resonance_reasoning}</p>
                  {analysisResults.oi.resonance_justification && (
                    <div className="mt-2 p-3 bg-white rounded border border-blue-200">
                      <p className="text-xs font-semibold text-blue-900">Why this score?</p>
                      <p className="text-xs text-slate-600 mt-1">{analysisResults.oi.resonance_justification}</p>
                    </div>
                  )}
                </div>

                {/* Synthesis */}
                <div className="p-5 bg-green-50 rounded-lg border-2 border-green-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-green-900">Synthesis (Weight: 20%)</span>
                      <p className="text-xs text-green-700 mt-1">How effectively does it combine concepts?</p>
                    </div>
                    <Badge className="bg-green-600 text-white text-lg px-4 py-2">
                      {analysisResults.oi.synthesis}/10
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-700 mb-2">{analysisResults.oi.synthesis_reasoning}</p>
                  {analysisResults.oi.synthesis_justification && (
                    <div className="mt-2 p-3 bg-white rounded border border-green-200">
                      <p className="text-xs font-semibold text-green-900">Why this score?</p>
                      <p className="text-xs text-slate-600 mt-1">{analysisResults.oi.synthesis_justification}</p>
                    </div>
                  )}
                </div>

                {/* Ethical */}
                <div className="p-5 bg-orange-50 rounded-lg border-2 border-orange-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-orange-900">Ethical Anchoring (Weight: 20%)</span>
                      <p className="text-xs text-orange-700 mt-1">How responsible and inclusive is it?</p>
                    </div>
                    <Badge className="bg-orange-600 text-white text-lg px-4 py-2">
                      {analysisResults.oi.ethical}/10
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-700 mb-2">{analysisResults.oi.ethical_reasoning}</p>
                  {analysisResults.oi.ethical_justification && (
                    <div className="mt-2 p-3 bg-white rounded border border-orange-200">
                      <p className="text-xs font-semibold text-orange-900">Why this score?</p>
                      <p className="text-xs text-slate-600 mt-1">{analysisResults.oi.ethical_justification}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Precedents & Innovations - Always Show Section */}
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Similar Precedents
                  </h4>
                  {analysisResults.oi.similar_precedents?.length > 0 ? (
                    <ul className="space-y-2">
                      {analysisResults.oi.similar_precedents.map((prec, idx) => (
                        <li key={idx} className="text-sm text-indigo-800 flex items-start gap-2">
                          <span className="text-indigo-500 mt-1">•</span>
                          <span>{prec}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-indigo-600 italic">No similar precedents identified - this may be highly novel!</p>
                  )}
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h4 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Key Innovations
                  </h4>
                  {analysisResults.oi.key_innovations?.length > 0 ? (
                    <ul className="space-y-2">
                      {analysisResults.oi.key_innovations.map((innov, idx) => (
                        <li key={idx} className="text-sm text-emerald-800 flex items-start gap-2">
                          <span className="text-emerald-500 mt-1">✦</span>
                          <span>{innov}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-emerald-600 italic">Innovation analysis pending</p>
                  )}
                </div>
              </div>

              {/* Improvement Advice */}
              {analysisResults.oi.improvement_advice?.length > 0 && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    How to Improve Your OI Score
                  </h4>
                  <ul className="space-y-2">
                    {analysisResults.oi.improvement_advice.map((advice, idx) => (
                      <li key={idx} className="text-sm text-amber-800">✓ {advice}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* MSA Card */}
          <Card className="mb-6 border-2 border-emerald-200">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-cyan-50">
              <div>
                <CardTitle className="flex items-center gap-2 text-2xl mb-2">
                  <Zap className="w-7 h-7 text-emerald-600" />
                  Mirror-Spark Assessment
                </CardTitle>
                <p className="text-sm text-slate-600">{analysisResults.msa.formula}</p>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* MSA Visualization */}
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-semibold text-blue-600">Mirror: {analysisResults.msa.mirror_pct}%</span>
                  <Badge className="bg-emerald-600 text-white text-lg px-4 py-2">
                    {analysisResults.msa.assessment}
                  </Badge>
                  <span className="text-sm font-semibold text-emerald-600">Spark: {analysisResults.msa.spark_pct}%</span>
                </div>
                <div className="h-8 bg-slate-200 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-blue-500 flex items-center justify-center text-white text-xs font-semibold"
                    style={{ width: `${analysisResults.msa.mirror_pct}%` }}
                  >
                    {analysisResults.msa.mirror_pct > 15 && 'Mirror'}
                  </div>
                  <div 
                    className="bg-emerald-500 flex items-center justify-center text-white text-xs font-semibold"
                    style={{ width: `${analysisResults.msa.spark_pct}%` }}
                  >
                    {analysisResults.msa.spark_pct > 15 && 'Spark'}
                  </div>
                </div>
              </div>

              {/* MSA Interpretation */}
              {analysisResults.msa.interpretation && (
                <div className="mb-6 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <h4 className="font-semibold text-cyan-900 mb-2">What This Means</h4>
                  <p className="text-sm text-cyan-800">{analysisResults.msa.interpretation}</p>
                </div>
              )}

              {/* Balance Advice */}
              {analysisResults.msa.balance_advice && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2">Balance Advice</h4>
                  <p className="text-sm text-amber-800">{analysisResults.msa.balance_advice}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions Card - Simplified since summary is shown at top */}
          <Card className="mb-6 border-2 border-slate-200">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50">
              <CardTitle className="flex items-center gap-2 text-xl">
                <ClipboardList className="w-6 h-6 text-slate-600" />
                Next Steps
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleDownloadReportPDF}
                  disabled={downloadingPDF || !structuredReport}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {downloadingPDF ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Preparing PDF...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download Full Report
                    </>
                  )}
                </Button>
                <Button 
                  onClick={handleEvaluateAsReport}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Deep Evaluate (Paper Mode)
                </Button>
                <Button 
                  onClick={handleGenerateReport}
                  variant="ghost"
                  disabled={generatingReport}
                  className="text-slate-600"
                >
                  {generatingReport ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Regenerate Summary
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              className="flex-1 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
              onClick={handleSave}
              size="lg"
            >
              <Save className="w-4 h-4 mr-2" />
              {challengeId ? 'Submit to Challenge' : 'Save Session'}
            </Button>
          </div>
        </div>

        {challengeId && showSubmissionModal && (
          <SubmissionModal
            open={showSubmissionModal}
            onClose={() => setShowSubmissionModal(false)}
            challengeId={challengeId}
            sessionId={`session-${Date.now()}`}
            oiScore={{
              total: analysisResults.oi.total,
              novelty: analysisResults.oi.novelty,
              resonance: analysisResults.oi.resonance,
              synthesis: analysisResults.oi.synthesis,
              ethical: analysisResults.oi.ethical
            }}
            msaScore={{
              mirrorPct: analysisResults.msa.mirror_pct,
              sparkPct: analysisResults.msa.spark_pct
            }}
            ideaTitle={sessionTitle}
            ideaDescription={Object.values(answers.spark || {}).join(' ')}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-xl font-bold">{sessionTitle}</h1>
              <p className="text-sm text-slate-600">{currentStage.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-save status indicator */}
            {autoSaveStatus === 'saving' && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            {autoSaveStatus === 'saved' && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>All changes saved</span>
              </div>
            )}
            {autoSaveStatus === 'error' && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <span>⚠️ Save failed</span>
              </div>
            )}
            {autoSaveStatus === 'quota' && (
              <div className="flex items-center gap-2 text-sm text-orange-600">
                <span>⚠️ Session limit reached</span>
                <Button variant="outline" size="sm" onClick={() => navigate('/pricing')} className="h-7 text-xs">
                  Upgrade
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      {challengeId && (
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3">
          <div className="container mx-auto px-6 flex items-center justify-center gap-3">
            <Trophy className="w-5 h-5" />
            <span className="font-semibold">Challenge Mode: Your solution will be analyzed and published</span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Challenge Banner */}
        {challengeData && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-5 h-5" />
                  <span className="font-semibold text-sm uppercase tracking-wider">Challenge Mode: SDG {challengeData.sdg}</span>
                </div>
                <h3 className="text-lg font-bold">{challengeData.title}</h3>
                <p className="text-sm text-purple-100 mt-1">{challengeData.sdg_name}</p>
              </div>
              <Badge className="bg-white/20 text-white border-white/30">
                {challengeData.difficulty}
              </Badge>
            </div>
          </div>
        )}
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-slate-600">
              Stage {currentStageIndex + 1} of {stages.length}
            </span>
            <span className="text-sm font-medium text-purple-600">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Stage Indicators */}
        <div className="flex gap-4 mb-8">
          {stages.map((stage, idx) => {
            const Icon = stage.icon;
            const isActive = idx === currentStageIndex;
            const isComplete = idx < currentStageIndex;
            
            return (
              <div
                key={stage.key}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  isActive
                    ? 'border-purple-500 bg-white shadow-lg'
                    : isComplete
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-200 bg-white/50'
                }`}
              >
                <Icon className={`w-6 h-6 mb-2 ${isActive ? 'text-purple-600' : isComplete ? 'text-green-600' : 'text-slate-400'}`} />
                <h3 className="font-semibold text-sm">{stage.title}</h3>
              </div>
            );
          })}
        </div>

        {/* Stage Content */}
        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${currentStage.color} flex items-center justify-center flex-shrink-0`}>
                <currentStage.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-2xl mb-2">{currentStage.title}</CardTitle>
                <p className="text-slate-600">{currentStage.description}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Session Persona Display - Read-only, shows selected personas and combined focus metrics */}
            {activePersonasData.length > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-slate-500">Session Persona{activePersonasData.length > 1 ? 's' : ''}:</span>
                  {activePersonasData.map(persona => (
                    <Badge key={persona.id} className={`bg-gradient-to-r ${persona.color} text-white border-0`}>
                      {persona.icon} {persona.name}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-xs text-slate-500 mr-1">Focus:</span>
                  {/* Show combined unique metrics from all personas */}
                  {[...new Set(activePersonasData.flatMap(p => p.metrics || []))].slice(0, 6).map((metric, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 bg-white rounded border border-slate-200 text-slate-600">
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Loading indicator while generating contextual questions */}
            {generatingQuestions && (
              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                <p className="text-purple-700 text-sm font-medium">
                  Personalizing questions for your idea...
                </p>
              </div>
            )}
            
            {/* Contextual questions badge */}
            {contextualQuestions && contextualQuestions[currentStage.key] && !generatingQuestions && (
              <div className="flex items-center gap-2 mb-4">
                <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Personalized Questions
                </Badge>
                <span className="text-xs text-slate-500">Tailored to your idea</span>
              </div>
            )}

            {prompts.map((prompt, index) => (
              <div key={index} className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  {index + 1}. {prompt}
                </label>
                <Textarea
                  value={answers[currentStage.key]?.[index] || ''}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder="Share your thoughts..."
                  className="min-h-[100px]"
                />
              </div>
            ))}

            <div className="flex gap-4 pt-4">
              {currentStageIndex > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStageIndex(currentStageIndex - 1)}
                >
                  Previous
                </Button>
              )}
              <Button
                className="flex-1 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
                onClick={handleNext}
                disabled={!isStageComplete()}
              >
                {currentStageIndex === stages.length - 1 ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze My Idea
                  </>
                ) : (
                  <>
                    Next Stage
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Floating Think Lab Button with Text */}
      {!showTitleInput && !showUploadOption && !showThinkLab && (
        <button
          onClick={() => setShowThinkLab(true)}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-500 text-white px-6 py-4 rounded-full shadow-2xl hover:scale-105 transition-all z-[100] flex items-center gap-3 animate-bounce"
          style={{
            animation: 'bounce 2s infinite'
          }}
        >
          <Lightbulb className="w-6 h-6" />
          <span className="font-semibold text-lg">Initiate Think Lab</span>
        </button>
      )}

      {/* Minimized Think Lab with Timer (when closed but timer active) */}
      {!showThinkLab && !showTitleInput && !showUploadOption && (
        <div className="fixed bottom-8 left-8 z-40">
          {/* Timer Widget */}
          {timerActive && timeRemaining > 0 && (
            <div className="bg-white rounded-lg shadow-2xl border-2 border-orange-400 p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Clock className="w-8 h-8 text-orange-600 animate-pulse" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 font-semibold">Active Timer</p>
                  <p className="text-2xl font-mono font-bold text-orange-600">
                    {formatTime(timeRemaining)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setTimerActive(false);
                    setTimeRemaining(null);
                  }}
                  className="ml-2 text-slate-400 hover:text-red-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <button
                onClick={() => setShowThinkLab(true)}
                className="w-full mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium"
              >
                Open Think Lab →
              </button>
            </div>
          )}

          {/* Track Reading Status */}
          {trackingEnabled && activeReading && (
            <div className="bg-green-50 rounded-lg shadow-lg border-2 border-green-400 p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <p className="text-xs font-semibold text-green-900">Tracking Reading</p>
              </div>
              <p className="text-xs text-green-800">
                {Math.floor((Date.now() - new Date(activeReading.startTime)) / 60000)} min elapsed
              </p>
            </div>
          )}
        </div>
      )}

      {/* Return from Reading Prompt */}
      {showReturnPrompt && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <Card className="w-full max-w-lg bg-white shadow-2xl animate-in zoom-in">
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Welcome Back!
                </h3>
                {activeReading && (
                  <p className="text-slate-600 mb-4">
                    You spent {Math.floor((Date.now() - new Date(activeReading.startTime)) / 60000)} minute(s) reading
                  </p>
                )}
              </div>

              {/* AI-Generated Summary */}
              {readingSummary && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                  <div className="flex items-start gap-2 mb-3">
                    <Brain className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 text-sm mb-2">📊 Reading Session Summary:</h4>
                      {activeReading && (
                        <div className="mb-3 p-2 bg-white rounded border border-blue-200">
                          <p className="text-xs text-slate-600 mb-1">
                            <strong>Resource:</strong> {activeReading.resource.title}
                          </p>
                          <p className="text-xs text-slate-600">
                            <strong>Time Spent:</strong> {Math.floor((Date.now() - new Date(activeReading.startTime)) / 60000)} min {Math.floor(((Date.now() - new Date(activeReading.startTime)) % 60000) / 1000)} sec
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-slate-800 leading-relaxed">{readingSummary}</p>
                    </div>
                  </div>
                </div>
              )}

              {!readingSummary && trackingEnabled && (
                <div className="mb-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-600 text-center">
                    <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                    Generating reading insights...
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setShowReturnPrompt(false);
                    setShowThinkLab(true);
                    setActiveTab('ideaboard');
                    // Stop timer
                    setTimerActive(false);
                    // Mark reading as complete
                    if (activeReading) {
                      setTrackedReadings(prev => [...prev, {
                        ...activeReading,
                        status: 'completed-early',
                        endTime: new Date(),
                        summary: readingSummary
                      }]);
                      setActiveReading(null);
                    }
                    setReadingSummary(null);
                  }}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                  size="lg"
                >
                  💡 Log My Thoughts
                </Button>
                
                <Button
                  onClick={() => {
                    setShowReturnPrompt(false);
                    setReadingSummary(null);
                    // Keep timer running, user continues session
                    toast({
                      title: "Timer Continues",
                      description: "Keep working! Timer is still running."
                    });
                  }}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  🔄 Continue Reading
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Think Lab Modal */}
      <ThinkLab
        sessionId={sessionTitle || 'temp-session'}
        sessionTitle={sessionTitle}
        isOpen={showThinkLab}
        onClose={() => setShowThinkLab(false)}
        timerActive={timerActive}
        setTimerActive={setTimerActive}
        timeRemaining={timeRemaining}
        setTimeRemaining={setTimeRemaining}
        trackingEnabled={trackingEnabled}
        setTrackingEnabled={setTrackingEnabled}
        activeReading={activeReading}
        setActiveReading={setActiveReading}
        trackedReadings={trackedReadings}
        setTrackedReadings={setTrackedReadings}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </div>
  );
};

export default NewThinkingSession;
